import { Box, Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";

import { useEffect, useMemo, useRef, useState } from "react";

import { useAppSelector } from "utils/redux/hooks";
import { selectAllChatbotConversations } from "app/containers/Chatbot/slice";
import { selectHasCoachBoConversationHistoryEnabled } from "app/containers/AdminConsole/slice";
import { selectCurrentUserInfo } from "app/containers/Global/slice";

import { useParams } from "../../../common";

import { MediumCustomIcon } from "../../layer00";

import { StyledTextField } from "../../layer01";

import { TeamDateRangeFilter } from "../../layer06";

import { BoBrainSectionList } from "../../layer21";

import boBrain from "../../../../assets/images/boBrain.png";

// Backend timestamps come back as either ISO or SQL ("yyyy-MM-dd HH:mm:ss").
const parseDate = (value: string) => DateTime.fromISO(value.replace(" ", "T"));

export const BoBrainActivityDetailSection = () => {
  const [search, setSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const params = useParams<{ focusSearch?: string }>();
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const conversations = useAppSelector(selectAllChatbotConversations);
  const isCompanyHistoryEnabled = useAppSelector(
    selectHasCoachBoConversationHistoryEnabled
  );
  const userInfo = useAppSelector(selectCurrentUserInfo);
  const isHistoryEnabled =
    isCompanyHistoryEnabled && !!userInfo?.hasCoachBoConversationHistoryEnabled;

  const lastUpdated = useMemo(() => {
    const latest = conversations
      .filter((c) => !c.weeklyCheckInLink && !!c.created_at)
      .map((c) => parseDate(c.created_at))
      .reduce<DateTime | null>(
        (max, date) => (!max || date > max ? date : max),
        null
      );
    return latest?.toFormat("LLLL d, yyyy") ?? null;
  }, [conversations]);

  // The nav's search icon routes here with focusSearch. Depending on the params
  // object (new on every navigate) refocuses even if we're already on this page.
  useEffect(() => {
    if (params.focusSearch) searchInputRef.current?.focus();
  }, [params]);

  return (
    <Stack direction="column" rowGap=".75rem" marginTop="2.5rem">
      <Stack
        direction="row"
        columnGap=".5rem"
        alignItems="center"
        paddingX=".5rem"
      >
        <Box
          component="img"
          src={boBrain}
          width="3.5rem"
          height="3.5rem"
          flexShrink={0}
          alignSelf="flex-start"
          sx={{
            mixBlendMode: "darken",
          }}
        />
        <Stack direction="column" rowGap=".25rem" minWidth={0}>
          <Typography variant="activityTitle">
            Your Conversation History
          </Typography>
          <Stack
            direction="row"
            columnGap=".5rem"
            alignItems="center"
            flexWrap="wrap"
          >
            {isHistoryEnabled && lastUpdated && (
              <Typography variant="activitySubtitle" color="#334C71">
                Last Updated: {lastUpdated}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        columnGap=".5rem"
        rowGap=".5rem"
        alignItems="center"
        flexWrap="wrap"
        paddingX=".5rem"
        paddingBottom=".5rem"
        marginTop=".75rem"
      >
        <Box flex="1 1 12rem" minWidth={0}>
          <StyledTextField
            placeholder="Search"
            value={search}
            inputRef={searchInputRef}
            sx={{
              marginTop: 0,
              paddingTop: 0,
              paddingBottom: 0,
              height: "2.5rem",
            }}
            startAdornment={<MediumCustomIcon icon="SEARCH" stroke="#99A5B8" />}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>
        <Box flex="0 0 auto">
          <TeamDateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        </Box>
      </Stack>
      <BoBrainSectionList
        search={search}
        startDate={startDate}
        endDate={endDate}
      />
    </Stack>
  );
};
